import { wrapIndex } from '../core/math';
import type { Track } from '../track/generate';
import type { SpeedProfile } from '../track/speedprofile';
import type { Corner } from '../track/brakingzones';

export type DrillMode = 'corner' | 'approach';

/**
 * Seconds of run-up before the braking point.
 *
 * Sized in *time*, not distance, and this is the single most important number in
 * the file. The original drill placed you a flat 35 m out, which at a fast
 * corner's entry speed is about one second between the camera snapping to a
 * scene you have never seen and the moment you must already be on the brakes.
 * Nobody can orient in that, which is what made the mode unplayable.
 */
export const RUN_UP_SECONDS = 3.2;
/** Floor for slow corners, where 3.2 s of run-up would be only a few metres. */
export const RUN_UP_MIN_METRES = 60;
/** Run-out past the exit, so you can see whether the exit was any good. */
export const RUN_OUT_METRES = 30;
/** A preceding corner closer than this dictates your entry speed. */
export const LINK_DISTANCE_METRES = 40;
/**
 * Caps on how far the approach may reach back.
 *
 * Without these the chain runs away: on a twisty circuit every corner links to
 * the one before it, so "drill T9" walked back through T8·T7·T6 to T5 and
 * produced a 21-second, five-corner segment. That is most of a lap, and a drill
 * that takes 21 seconds to retry is not a drill any more — attempts per minute
 * is the entire point.
 */
export const MAX_LINKED_CORNERS = 2;
export const MAX_SEGMENT_SECONDS = 14;

export interface DrillSegment {
  corner: Corner;
  mode: DrillMode;
  startIndex: number;
  brakeIndex: number;
  apexIndex: number;
  endIndex: number;
  /** Total segment length, m. */
  lengthM: number;
  /** Fastest possible time through the segment, ms. */
  idealMs: number;
  /** Corners covered, for the label. Usually one; more in approach mode. */
  covered: number[];
  label: string;
}

/** Walk forward from `from` to `to`, returning every index inclusive. */
function span(from: number, to: number, n: number): number[] {
  const out: number[] = [];
  let i = from;
  for (let guard = 0; guard < n; guard++) {
    out.push(i);
    if (i === to) break;
    i = wrapIndex(i + 1, n);
  }
  return out;
}

/** Forward distance from `from` to `to` along the centerline, m. */
function forwardDistance(from: number, to: number, n: number, ds: number): number {
  return wrapIndex(to - from, n) * ds;
}

/**
 * Corners whose exit sits within `LINK_DISTANCE_METRES` of the next one's
 * braking point, walking backwards. On a twisty circuit T3/T4/T5 all have ~4 m
 * braking zones because you never regain speed between them — drilling one in
 * isolation would drop you mid-complex at a speed you could not have carried
 * there yourself.
 */
function linkedPredecessors(track: Track, corners: Corner[], corner: Corner): Corner[] {
  const n = track.center.length;
  const ordered = [...corners].sort((a, b) => a.number - b.number);
  const chain: Corner[] = [];

  let current = corner;
  for (let guard = 0; guard < ordered.length; guard++) {
    const idx = ordered.findIndex((c) => c.number === current.number);
    const previous = ordered[(idx - 1 + ordered.length) % ordered.length];
    if (previous.number === current.number) break;
    if (chain.some((c) => c.number === previous.number)) break;

    const gap = forwardDistance(previous.exitIndex, current.brakeIndex, n, track.spacing);
    if (gap > LINK_DISTANCE_METRES) break;

    chain.unshift(previous);
    current = previous;
  }
  return chain;
}

/** Builds the segment for an explicit chain of preceding corners. */
function segmentFor(
  track: Track,
  profile: SpeedProfile,
  corner: Corner,
  mode: DrillMode,
  chain: Corner[],
): DrillSegment {
  const n = track.center.length;
  const ds = track.spacing;
  const first = chain[0] ?? corner;

  // Run-up measured back from wherever braking first begins.
  const entrySpeed = Math.max(6, profile.vIdeal[first.brakeIndex]);
  const runUpM = Math.max(RUN_UP_MIN_METRES, entrySpeed * RUN_UP_SECONDS);
  const startIndex = wrapIndex(first.brakeIndex - Math.round(runUpM / ds), n);
  const endIndex = wrapIndex(corner.exitIndex + Math.round(RUN_OUT_METRES / ds), n);

  // Fastest possible time through the segment — the same ds/v integral the lap
  // solver uses, restricted to this stretch.
  const indices = span(startIndex, endIndex, n);
  let idealS = 0;
  for (let k = 0; k < indices.length - 1; k++) {
    const a = profile.vIdeal[indices[k]];
    const b = profile.vIdeal[indices[k + 1]];
    idealS += ds / Math.max(0.5, (a + b) / 2);
  }

  const covered = [...chain.map((c) => c.number), corner.number];
  const label =
    covered.length > 1 ? `T${covered[0]}–T${covered[covered.length - 1]}` : `T${corner.number}`;

  return {
    corner,
    mode,
    startIndex,
    brakeIndex: first.brakeIndex,
    apexIndex: corner.apexIndex,
    endIndex,
    lengthM: (indices.length - 1) * ds,
    idealMs: idealS * 1000,
    covered,
    label,
  };
}

export function buildDrill(
  track: Track,
  profile: SpeedProfile,
  corners: Corner[],
  corner: Corner,
  mode: DrillMode,
): DrillSegment {
  let chain = mode === 'approach' ? linkedPredecessors(track, corners, corner) : [];

  // Keep only the nearest few predecessors, then drop from the front until the
  // segment is short enough to retry comfortably.
  if (chain.length > MAX_LINKED_CORNERS) chain = chain.slice(-MAX_LINKED_CORNERS);

  let segment = segmentFor(track, profile, corner, mode, chain);
  while (chain.length > 0 && segment.idealMs > MAX_SEGMENT_SECONDS * 1000) {
    chain = chain.slice(1);
    segment = segmentFor(track, profile, corner, mode, chain);
  }
  return segment;
}

/** True when this corner has a linked predecessor worth including. */
export function hasApproach(track: Track, corners: Corner[], corner: Corner): boolean {
  return linkedPredecessors(track, corners, corner).length > 0;
}

export interface DrillResult {
  timeMs: number;
  idealMs: number;
  /** Slowest point through the corner, m/s. */
  vMin: number;
  vMinIdeal: number;
  /** Metres between your braking point and the ideal one. Negative is early. */
  brakeOffset: number;
  locked: boolean;
  wentOff: boolean;
  /** True when this attempt beat every previous one this session. */
  personalBest: boolean;
}

/** One-line verdict for the flash overlay. */
export function describeDrill(r: DrillResult): string {
  if (r.wentOff) return 'ran wide';
  if (r.locked) return 'locked up';
  if (r.brakeOffset < -6) return `braked ${Math.abs(Math.round(r.brakeOffset))}m early`;
  if (r.brakeOffset > 6) return `braked ${Math.round(r.brakeOffset)}m late`;
  const gap = (r.vMinIdeal - r.vMin) * 3.6;
  if (gap > 4) return `${Math.round(gap)} km/h slow at the apex`;
  return 'clean';
}
